import axios from 'axios';
import authEvents from './authEvents';
import tokenValidator, { cleanupAuthStorage, markSessionExpired } from './tokenValidator';
import { renewSession, isSessionRejection } from './sessionRenewal';

// Set the base URL for all axios requests using environment variable
axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL || '';

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];
const MAX_QUEUE_SIZE = 50; // Prevent memory leaks from unbounded queue growth

// Puts a fresh pass on a request being retried. Some callers of the global instance send their own
// `x-auth-token` header, which the server reads FIRST — so it is replaced too, or the retry would resend
// the expired pass.
const setPass = (request, token) => {
  request.headers.Authorization = `Bearer ${token}`;
  if (request.headers?.['x-auth-token']) {
    request.headers['x-auth-token'] = token;
  }
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Add request interceptor for auth token and logging
axios.interceptors.request.use(
  (config) => {
    // Skip auth header for public endpoints
    const isPublicEndpoint =
      config.url?.includes('/auth/') ||
      config.url?.includes('/api/v1/public');

    if (!isPublicEndpoint) {
      // Only add auth token if it's valid
      const token = tokenValidator.getValidToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for logging and auth handling with refresh capability
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(`API Error ${error.response.status} from ${error.config.url}`);
      }

      // Handle 401 authentication errors with refresh token retry
      if (error.response.status === 401 && !originalRequest._retry) {
        // Skip refresh for login/refresh endpoints to prevent loops
        if (originalRequest.url?.includes('/login') || originalRequest.url?.includes('/refresh')) {
          return Promise.reject(error);
        }

        // If already refreshing, queue this request
        if (isRefreshing) {
          // Prevent unbounded queue growth (potential memory leak)
          if (failedQueue.length >= MAX_QUEUE_SIZE) {
            console.error('❌ Request queue full, rejecting request');
            return Promise.reject(new Error('Too many concurrent refresh requests'));
          }

          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              setPass(originalRequest, token);
              return axios(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Shared, tab-safe renewal (utils/sessionRenewal.js); the pass this request carried is the
          // one known to be no good.
          const failedPass = originalRequest.headers?.['x-auth-token']
            || String(originalRequest.headers?.Authorization || '').replace(/^Bearer /, '')
            || null;
          const accessToken = await renewSession(failedPass);

          // Update the failed request with new token
          setPass(originalRequest, accessToken);

          // Process queued requests
          processQueue(null, accessToken);
          isRefreshing = false;

          // Retry the original request
          return axios(originalRequest);
        } catch (refreshError) {
          console.error('❌ Token refresh failed:', refreshError);

          processQueue(refreshError, null);
          isRefreshing = false;

          // Sign out ONLY when the session is over (refused, or nothing to renew with). A network error
          // or server fault used to sign people out here too.
          if (isSessionRejection(refreshError)) {
            cleanupAuthStorage();
            markSessionExpired();
            authEvents.emitLogoutRequired();
          }
          return Promise.reject(refreshError);
        }
      } else if (error.response.status === 403) {
        // User doesn't have permission
        console.error('Access denied: You do not have permission to access this resource');
      }
    } else {
      console.error(`API Error: ${error.message}`);
    }
    return Promise.reject(error);
  }
);

export default axios;
